import React, { useState } from 'react'
import { X, Mail, ArrowRight, CheckCircle } from 'lucide-react'
import { Button } from './Button'
import { Input } from './Input'
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'

interface EmailCaptureModalProps {
  isOpen: boolean
  onClose: () => void
  onEmailCaptured: (email: string) => void
}

export const EmailCaptureModal: React.FC<EmailCaptureModalProps> = ({
  isOpen,
  onClose,
  onEmailCaptured
}) => {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  useBodyScrollLock(isOpen)

  const validateEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(emailValue)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const trimmedEmail = email.trim().toLowerCase()

    if (!trimmedEmail) {
      setError('Please enter your email address')
      return
    }

    if (!validateEmail(trimmedEmail)) {
      setError('Please enter a valid email address')
      return
    }

    setIsSubmitting(true)

    try {
      const capturedEmails = JSON.parse(localStorage.getItem('captured_emails') || '[]')
      if (!capturedEmails.includes(trimmedEmail)) {
        capturedEmails.push(trimmedEmail)
        localStorage.setItem('captured_emails', JSON.stringify(capturedEmails))
      }

      setTimeout(() => {
        setIsSubmitting(false)
        onEmailCaptured(trimmedEmail)
      }, 300)
    } catch (err) {
      setIsSubmitting(false)
      setError('Something went wrong. Please try again.')
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (error) {
      setError('')
    }
  }

  if (!isOpen) return null

  return (
    <div 
      className="modal-overlay"
      style={{ zIndex: 10000 }}
    >
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 relative animate-in fade-in zoom-in duration-200">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
        >
          <X className="h-5 w-5 text-slate-500 dark:text-slate-400" />
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-[#69adba] to-[#5a9aa6] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Mail className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
            Get Started
          </h2>
          <p className="text-slate-600 dark:text-slate-400">
            Enter your email to begin your 1031 exchange journey with Neela
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Email Address
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={handleChange}
              placeholder="you@example.com"
              className={`w-full ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
              autoFocus
              disabled={isSubmitting}
            />
            {error && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                <X className="h-4 w-4" />
                {error}
              </p>
            )}
            {!error && email && validateEmail(email.trim()) && (
              <p className="mt-2 text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                Valid email address
              </p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#69adba] hover:bg-[#5a9aa6] text-white"
              disabled={isSubmitting || !email.trim()}
            >
              {isSubmitting ? (
                'Starting...'
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

